import { DetailOverlay, Label, MonoMeta, ItemEditorBar, NoteOnCard } from "@/design/primitives";
import { GoalHeading, GoalStateLine, GoalNext } from "./GoalCard";
import { RunnerLive } from "@/screens/projects/RunnerLive";

const PLAN_HEADINGS = ["What this chunk is", "Why it matters", "What Kit uses to do this", "Planned steps"];
const CLOSE_HEADINGS = ["Close-out left", "Success checks verified"];

// Jeff 10/29: "when I click on a project… I can't tell what's being worked on."
// The runner data (live phase status, per-phase model attribution, lanes, budget)
// exists behind /api/project/<slug>/runner and RunnerLive already renders it on the
// Projects board — it just was never mounted here. Shown whenever the card is in a
// working state; data rules are RunnerLive's own (payload truth, no inference).
const RUNNER_STATES = new Set(["EXECUTE", "TEST", "PLAN"]);

export const GoalDetail = ({ card, variant = "desktop", onClose, editor }) => {
  const skills = card.detail.skills?.length ? card.detail.skills.join(" · ") : "the usual build kit";
  const phases = card.detail.phases?.length ? card.detail.phases : ["The steps aren't written down yet"];
  const { checked, total } = card.detail.successTest || { checked: 0, total: 0 };
  const closeLeft = (card.detail.closeChecklist?.total || 0) - (card.detail.closeChecklist?.checked || 0);

  return (
    <DetailOverlay variant={variant === "desktop" ? "panel" : "sheet"} onClose={onClose} label={`${card.title} details`} testId="goal-detail">
      <GoalHeading card={card} as="h2" className="pr-12 pt-2" />
      <GoalStateLine card={card} className="mt-4" />
      <GoalNext card={card} className="mt-4" />
      {RUNNER_STATES.has(card.rawState) && <RunnerLive slug={card.slug} />}
      <div className="mt-6 border-t border-os-border">
        <section data-testid="goal-detail-section-1" className="border-b border-os-border py-4">
          <Label as="h3">What this chunk is</Label>
          <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">{card.goal}</p>
        </section>
        <section data-testid="goal-detail-section-2" className="border-b border-os-border py-4">
          <Label as="h3">Why it matters</Label>
          <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">{card.why}</p>
        </section>
        <section data-testid="goal-detail-section-3" className="border-b border-os-border py-4">
          <Label as="h3">What Kit uses to do this</Label>
          <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">{skills}</p>
        </section>
        <section data-testid="goal-detail-section-4" className="border-b border-os-border py-4">
          <Label as="h3">Planned steps</Label>
          <ol className="mt-1.5 flex flex-col gap-1.5">
            {phases.map((phase, i) => (
              <li key={i} data-testid={`goal-detail-phase-${i + 1}`} className="text-[0.875rem] leading-[1.5] text-os-ink-2">
                {phase}
              </li>
            ))}
          </ol>
          <MonoMeta as="p" className="mt-2">{`${card.phaseCount} planned steps`}</MonoMeta>
        </section>
        {total > 0 && (
          <section data-testid="goal-detail-section-5" className="border-b border-os-border py-4">
            <Label as="h3">Success checks verified</Label>
            <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">
              {checked} of {total} checks verified so far{closeLeft > 0 ? ` · close-out: ${closeLeft} items left` : ""}
            </p>
          </section>
        )}
        {((card.payload?.jeff || card.detail?.jeff || {}).notes || []).length > 0 && (
          <section data-testid="goal-detail-notes" className="border-b border-os-border py-4">
            <Label as="h3">Your notes on this project</Label>
            <ul className="mt-1.5 flex flex-col gap-1.5">
              {(() => {
                const seen = new Set();
                const prior = ((card.payload?.jeff || card.detail?.jeff || {}).notes || []).filter((n) => {
                  const k = `${n.at}|${n.note}`;
                  if (seen.has(k)) return false;
                  seen.add(k);
                  return true;
                });
                return prior.map((n, i) => (
                  <li key={i} className="text-[0.875rem] leading-[1.5] text-os-ink-2">
                    <span className="mr-1.5 font-mono text-[0.6875rem] text-os-ink-3">
                      {String(n.at || "").replace("T", " ").slice(0, 16)}
                    </span>
                    {n.note}
                  </li>
                ));
              })()}
            </ul>
          </section>
        )}
      </div>
      {editor && (
        <>
          <ItemEditorBar
            board={editor.board}
            item={editor.item}
            onClose={onClose}
            testId={`item-editor-goal-${card.slug}`}
          />
          {editor.board?.live && (
            <div className="mt-3">
              <NoteOnCard board={editor.board} slug={editor.item.slug} testId={`goal-detail-note-${card.slug}`} />
            </div>
          )}
        </>
      )}
    </DetailOverlay>
  );
};